///
/// EarthBrowser
///
/// TroughRidgeNode - one 500 hPa trough or ridge axis on the height shell,
/// in the chart convention: a dashed line for a trough, a zigzag for a
/// ridge. The axis vertices arrive one per grid row; they are smoothed,
/// resampled by arc length and then styled, so the dash and zigzag pitch
/// is uniform along the axis. The shell altitude follows the camera (see
/// shellAltitude.ts), so setAltitude() rebuilds the world positions.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { rgba, type color } from "@stovokor/math/color"
import { cross, normal, type vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { slerpUnit, surfacePoint, unitNormal } from "../hurricanes/stormTrack"
import { type AxisFeature, type AxisPoint } from "../weather-grid/TroughRidgeDetector"

const cTroughColor: color = rgba(1.0, 0.72, 0.32, 0.9)
const cRidgeColor: color = rgba(1.0, 0.82, 0.45, 0.9)
const cWidth = 2.5
const cSampleKm = 40         // resample pitch along the axis
const cDashKm = 160          // trough: dash + gap pattern
const cGapKm = 90
const cZigPitchKm = 220      // ridge: one zig per this many km
const cZigAmplitudeKm = 55

export class TroughRidgeNode extends NodeBase {
	private readonly lines: { node: PolylineNode, units: vec3[] }[] = []
	private altitudeKm = NaN

	constructor(context: StovokorContext, public readonly axis: AxisFeature, altitudeKm: number) {
		super()
		const smoothed = smooth(axis.points)
		const samples = resampleUnits(smoothed, cSampleKm)
		if (samples.length < 2) return

		const color = axis.type === 'trough' ? cTroughColor : cRidgeColor
		if (axis.type === 'trough') {
			// Dash runs along the resampled chain
			const dashN = Math.max(1, Math.round(cDashKm / cSampleKm))
			const gapN = Math.max(1, Math.round(cGapKm / cSampleKm))
			for (let i = 0; i < samples.length - 1; i += dashN + gapN) {
				const end = Math.min(samples.length - 1, i + dashN)
				if (end - i < 1) continue
				this.addLine(context, samples.slice(i, end + 1), color)
			}
		} else {
			// Zigzag: offset alternate vertices sideways across the axis
			const pitchN = Math.max(1, Math.round(cZigPitchKm / cSampleKm / 2))
			const amp = cZigAmplitudeKm / 6371
			const zig: vec3[] = []
			for (let i = 0; i < samples.length; i++) {
				const phase = Math.floor(i / pitchN) % 2 === 0 ? 1 : -1
				const a = samples[Math.max(0, i - 1)]
				const b = samples[Math.min(samples.length - 1, i + 1)]
				const tangent = normal(b.subtract(a))
				const side = normal(cross(tangent, samples[i]))
				zig.push(normal(samples[i].add(side.multiply(phase * amp))))
			}
			this.addLine(context, zig, color)
		}

		this.setAltitude(altitudeKm)
	}

	private addLine(context: StovokorContext, units: vec3[], color: color): void {
		const node = new PolylineNode(context, {
			positions: units.map(u => surfacePoint(u, 0)),
			width: cWidth,
			color,
			depthWrite: false,
		})
		this.lines.push({ node, units })
		this.addNode(node)
	}

	/** Move the axis to the shell altitude (no-op when unchanged) */
	public setAltitude(altitudeKm: number): void {
		if (Math.abs(altitudeKm - this.altitudeKm) < 0.05) return
		this.altitudeKm = altitudeKm
		for (const line of this.lines) {
			line.node.updatePositions(line.units.map(u => surfacePoint(u, altitudeKm)))
		}
	}
}

/** Three-point moving average on longitude (unwrapped) and latitude */
function smooth(points: AxisPoint[]): AxisPoint[] {
	if (points.length < 3) return points
	const lons = points.map(p => p.lon)
	for (let i = 1; i < lons.length; i++) {
		while (lons[i] - lons[i - 1] > 180) lons[i] -= 360
		while (lons[i] - lons[i - 1] < -180) lons[i] += 360
	}
	const out: AxisPoint[] = []
	for (let i = 0; i < points.length; i++) {
		const a = Math.max(0, i - 1)
		const b = Math.min(points.length - 1, i + 1)
		out.push({
			lon: (lons[a] + lons[i] + lons[b]) / 3,
			lat: (points[a].lat + points[i].lat + points[b].lat) / 3,
		})
	}
	return out
}

/** Unit directions along the axis at a uniform great-circle pitch */
function resampleUnits(points: AxisPoint[], pitchKm: number): vec3[] {
	const units = points.map(p => unitNormal(p.lat, p.lon))
	const step = pitchKm / 6371
	const out: vec3[] = [units[0]]
	let carry = 0
	for (let i = 1; i < units.length; i++) {
		const a = units[i - 1]
		const b = units[i]
		const cosA = Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z))
		const seg = Math.acos(cosA)
		let t = step - carry
		while (t <= seg) {
			out.push(slerpUnit(a, b, t / seg))
			t += step
		}
		carry = seg - (t - step)
	}
	return out
}
